import { FeathersService, Paginated, Query } from "@feathersjs/feathers/lib";
import { IReactionDisposer, makeAutoObservable, reaction, runInAction, when } from "mobx";
import { computedFn } from "mobx-utils";
import { rootStore } from "src/app";
import { LoadingStatus } from "src/types";
import { deepCmp } from "src/util/util";
import { EventHookChain } from ".";
import { EventHookMethod } from "./baseDataStore";

export type ContainerConfig<I extends {id?: number}, O extends {id?: number} = I> = {
  paginate?:   boolean,
  pageSize?:   number,
  extendPage?: boolean,
  limited?:    boolean,              // TODO implement this to work! Check in updates and disable further loads if limit is reached
  sortProp?:   keyof O,
  sort?:       number,               // TODO check if the sort order is correct!
  name?:       string,
  params?:     {[key: string]: any}  // If the query is a function rather than a Query object it will be called with this object as parameter to create a query object
  conversion?:    (data: I) => O,
  eventFilter?:   (data: I, params?: {[key: string]: any}) => boolean,
  eventCallback?: (method: string, data: I, params?: {[key: string]: any}) => void,
  callback?:      (data: O) => void,
}


/**
 * Temporary container for dataBase Items that do not need to persist through out
 * several view. Data will be loaded according to the initially provided @query with pagination 
 * handled accordingly. Data will always be sorted according to @sortProp
 * 
 * Realtime updates can be provided when hooking up the @update method with the evenListeners in the
 * store the container belongs to. 
 * 
 * Note: To ensure canLoad is working this has to be setup after intializing all stores! 
 *       As such, dont create these in the constructor but rather in the init method of the stores. 
 * 
 * Note: Remember to set @sort even if @query is a custom function since its used when updates come in to resort.
 * 
 * @type  I           Generic entry type of the incoming data. I has to be an object with 
 *                    at least an id prop of type number. This id is used to map entries in the container.
 *                    Note, that making id optional is still necessary to allow IData to work. 
 * @type  O           Generic entry type of the container. O has to be an object with 
 *                    at least an id prop of type number. This id is used to map entries in the container.
 *                    Note, that making id optional is still necessary to allow IData to work. 
 * @param query       Query used to load data. If a function is passed, @config.params is passed to this function to generate a query on every load.
 *                    Changing these params, triggers a reload. When a string is passed it is assumed that this string is the name of a custom service
 *                    method that is called rather than @service.find. and @config.params is passed to this method instead. 
 * @param service     Feathers services that data is loaded from.
 * @param array | false     Decide if the container used is an id indexed object or an array to preserve the order in which data is loaded.
 * @param paginate | true   If true, the container will load one page of size @pageSize at a time.
 * @param pageSize | 100    Size of loaded pages. Ignored if @paginate is false.
 * @param extendPage | true If true, the data of a new page will be apanded to the container. If false, the old data is discarded. 
 * @param limited | false   If true, container will limit the number of items to at most one @pageSize . Ignored if @paginate is false. 
 * @param sortProp | "date" Prop that is used for sorting. To "disable" sorting, pass "id" as sortProp.
 * @param sort | 1          Determins whether the containers are sorted ascending or descending
 * @param callback      An optional call back that is called with each item when its loaded.
 * @param eventCallback An optional call back that is called when an update passed the eventFilter. 
 * @param conversion    Converts service find result of type @I into container entries of type @O .
 * @param condition     Returns true for data iff the data matches @query . Use () => false to ignore updates.
 */
export class DataContainer<I extends {id?: number}, O extends {id?: number} = I> {
  private   _loadId: number = 0;
  private   page:    number = 0;
  protected total:   number = 0;
  protected eventQueue: {data: I, method: EventHookMethod}[] = []; // If events come in while the container is loading they will be stored here
  protected disposer: IReactionDisposer = null; // Mobx When disposed for processing the eventQueue

  protected readonly defaultConfig: ContainerConfig<I, O> = {
    paginate:    true,
    pageSize:    100,
    sort:        1,
    extendPage:  true,
    limited:     false,
    sortProp:    "date" as keyof O,
    //@ts-ignore path exists on service eventhough the type does not contain it
    name: this.service.path + "_storage",
    conversion:  (d: I) => d as unknown as O,
    eventFilter: () => true,
  }

  public status:    LoadingStatus     = LoadingStatus.None;
  public container: {[id: number]: O} = {};
  public array:     O[]               = [];
  public name:      string            = "";                  // for debugging only

  constructor(
    protected query: Query | ((params: {[key: string]: any}) => Query) | string, 
    protected readonly service: FeathersService<any>,
    protected readonly config?: ContainerConfig<I, O>
  ) {

    // Note: If this is ever used as a base class for other containers this has
    // to be changed to makeObservable(this, ...) and things need to be declared 
    // manually
    makeAutoObservable(this);

    // If a container is initialized and asked to load before the user is logged in or before 
    // a query is set it goes into a Waiting status. 
    // This reaction makes sure that the load is continued as soon as its possible.
    reaction(() => this.canLoad, () => {
      if(this.canLoad && rootStore.authStore.isLoggedIn && this.status === LoadingStatus.Waiting) {
        this.status = LoadingStatus.None;
        this.load();
      }
    });

    this.config = Object.assign({}, this.defaultConfig, this.config);
    this.name = this.config.name;
  }

  /*******************************************************************************************************************************************
   * DB Calls 
   ********************************************************************************************************************************************/
  /**
   * Load initial data of the first page if this.config.paginate is true. Loads all data otherwise.
   * Repeated calls proceed to the next page if available.
   */
  load = async () => {
    if(!this.canLoad) {
      this.status = LoadingStatus.Waiting;
      return;
    }
    if( this.config.paginate && this.status === LoadingStatus.Loading) return;
    if(!this.config.paginate && [LoadingStatus.Loading, LoadingStatus.Finished].includes(this.status)) return;

    let data:  Paginated<any>;

    // Make sure that only latest load of colliding requests is executed
    this._loadId = (this._loadId+1) % 10000;
    const loadId = this._loadId;

    let finalQuery;
    try {
      this.status = LoadingStatus.Loading;
      if(typeof this.query === "string") {
        if(this.config.paginate) {
          this.config.params.$limit = this.config.pageSize;
          this.config.params.$skip  = this.page * this.config.pageSize;
        }
        //@ts-ignore TODO figure out feathers types here
        data = await this.service[this.query](this.config.params) as unknown as Paginated<any>;
      } else {
        const _query = typeof this.query === "function" ? this.query(this.config.params) : this.query;
        const query: Query  = _query["query"] ? _query : {query: _query};
  
        finalQuery = {
          ...query,
          query: {
            $limit: this.config.pageSize,
            $sort:  {[this.config.sortProp]: this.config.sort},
            $skip:  this.page * this.config.pageSize,
            ...query.query,
          }
        }
        data = (await this.service.find(finalQuery)) as unknown as Paginated<any>;
      }
    } catch (e) {
      runInAction(() => this.status = LoadingStatus.None);
      // @ts-ignore Type definition doesnt contain the path prop
      console.error("Data load failed", this.name, this.service.path, finalQuery, e.message);
      return;
    }

    if(loadId !== this._loadId) return;
    
    if(!data) { // Some custom methods can return null if they fail
      data = {
        total: 0,
        data:  [],
        limit: this.config.pageSize,
        skip:  this.page * this.config.pageSize
      }
    }
    runInAction(() => {
      this.total    = data.total;
      const entries = data.data ? data.data.map(d => {
        const entry = this.config.conversion(d) as O;
        this.config.callback && this.config.callback(entry);
        return entry;
      }) : [];
      if(!this.config.extendPage) {
        this.container  = {};
        this.array      = [];
      }
      this.array = this.array.concat(entries);
      this.container = Object.assign({}, this.container, entries.reduce((res: {[id: number]: O}, val) => {
        res[val.id] = val;
        return res;
      }, {}));
    });

    // Note that pagination is enabled by default on the server. 
    // In case pagination should not be used for this container, 
    // keep loading data until everything is loaded. 
 
    if(!this.config.paginate && (++this.page)*this.config.pageSize < data.total) {
      runInAction(() => {
        this.status = LoadingStatus.Next;
      });
      this.load();
    } else if (this.config.paginate) {
      runInAction(() => {
        this.status = LoadingStatus.Next;
      });
    } else {
      runInAction(() => {
        this.status = LoadingStatus.Finished;
      });
    }
  }

  public addEventCallbacks = (eventCallBacks?: EventHookChain<I>, methods?: EventHookMethod[]) => {
    (methods || ["created", "removed", "updated", "patched"]).forEach((m: EventHookMethod) => {
      eventCallBacks ? 
        eventCallBacks.after[m].push((data: I) => this.update(data, m)) 
        : this.service.on(m, (data: I) => this.update(data, m));
    });
  }
  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  /**
   * Sets the search query of this container. Changing the query clears the content. 
   * 
   * @param query  New query to be used
   * @param noLoad If true, even if the container is cleared, no reload is issued
   */
  setQuery = (query: Query | ((params: {[key: string]: any}) => Query), noLoad?: boolean): void => {
    if(deepCmp(this.query, query)) return;
    this.query = query;
    if(this.status !== LoadingStatus.None) {
      this.clear();
      !noLoad && this.load();
    }
  }

  /**
   * If a function was provided as query, this sets new parameters for that function. Otherwise, works the same 
   * as setQuery. 
   * 
   * @param query  New query parameters to be used
   * @param noLoad If true, even if the container is cleared, no reload is issued
   */
  setQueryParams = (params: {[key: string]: any}, noLoad?: boolean) : void => {
    if(deepCmp(this.config.params, params)) return;
    this.config.params = params;
    if(this.status !== LoadingStatus.None) {
      this.clear();
      !noLoad && this.load();
    }
  }
  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  get getNumItems(): number {
    if(![LoadingStatus.Finished, LoadingStatus.Next].includes(this.status)) return 0;
    return this.array.length;
  }
  /**
   * @retun true if the storage container has pagination enabled and there is more to load than currently loaded. 
   */
  get hasMore(): boolean {
    return this.config.paginate && this.total > this.getNumItems;
  }
  /**
   * To be able to load anything the user has to be logged in and a query has to be set. 
   */
  get canLoad(): boolean {
    return Boolean(this.query) &&
      (typeof this.query !== "function" || Boolean(this.config.params)) &&
      !!rootStore && 
      rootStore.authStore?.isLoggedIn && 
      this.status !== LoadingStatus.Waiting;
  }
  /**
   * @return the complete content of the container
   */
  get getItems(): {[id: number]: O} {
    return this.container;
  }

  get getArray(): O[] {
    return this.array;
  }

  /**
   * @return the item of the container with @id
   */
  getItemById = computedFn((id: number): O => {
    return this.container[id];
  });

  /**
   * @return loading status of the container. 
   *        'None' until loading was initiated. 
   *        'Loading' after loading started until all current pages were loaded.
   *        'Finished' when all current pages were loaded successfully.
   */
  get getStatus(): LoadingStatus {
    return this.status;
  }

  get getTotal(): number {
    return Number(this.total);
  }

  get getPage(): number {
    return Number(this.page);
  }

  get getParams(): {[key: string]: any} {
    return this.config.params;
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  /**
   * Clears Data from this.container.
   */
  clear = () => {
    this.container  = {};
    this.array      = [];
    this.eventQueue = [];
    this.page       = 0;
    this.total      = 0;
    this.status     = LoadingStatus.None;
  }

  protected addItem = (data: O, idx?: number) => {
    runInAction(() => {
      this.container[data.id] = data;
      if(idx != undefined && idx !== -1) {this.array[idx] = data; this.array = this.array.slice();}
      else this.array = this.array.concat([data]);

      //@ts-ignore Typescript typing here gets annoying with the generics...it works ok?!
      this.array.sort((a: O, b: O) => (a[this.config.sortProp] - b[this.config.sortProp]) * this.config.sort);
    });
  }

  protected removeItem = (data: O, idx: number) => {
    runInAction(() => {
      delete this.container[data.id];
      this.array = this.array.filter((val, index) => index !== idx);
    });
  }

  /**
   * Updates are prefiltere on the backend (see backend/src/channels.ts). But a final filtering has 
   * to occur in the container itself.
   * Filtering is done based on pagination and this.config.eventFilter. 
   * 
   * TODO add pagination filtering! Check if the storage is supposed to hold only a limited number of items
   * and make sure to discard accordingly if there are too many 
   */
  update = (data: I, method: EventHookMethod) => {
    if(!data.id) return;
    else if(this.status === LoadingStatus.None) return;
    else if(this.status !== LoadingStatus.Finished && this.status !== LoadingStatus.Next) {
      this.eventQueue.push({data: data as I, method});
      if(!this.disposer) this.disposer = when(() => this.status === LoadingStatus.Finished, this.updateQueue);
      return;
    }
    const idx = this.array.findIndex(d => Number(d.id) === Number(data.id));
    switch(method) {
      case "updated":
      case "patched":
        if(this.config.eventFilter(data, this.config.params)) {
          this.config.eventCallback && this.config.eventCallback("updated", data, this.config.params);
          const newData = this.config.conversion(data);
          this.addItem(newData, idx === -1 ? undefined : Number(idx));
        }
        else if(idx !== -1) {
          this.config.eventCallback && this.config.eventCallback("removed", data, this.config.params);
          const newData = this.config.conversion(data);
          this.removeItem(newData, Number(idx));
        }
        break;
      case "created":
        if(idx !== -1 || !this.config.eventFilter(data, this.config.params)) return;
        else {
          this.config.eventCallback && this.config.eventCallback("created", data, this.config.params);
          const newData = this.config.conversion(data);
          this.addItem(newData);
        }
        break;
      case "removed":
        if(idx === -1) return;
        else {
          this.config.eventCallback && this.config.eventCallback("removed", data, this.config.params);
          const newData = this.config.conversion(data);
          this.removeItem(newData, Number(idx))
        }
        break;
      default:
        console.error("Incorrect event method", method);
        return;
    }
  }

  /**
   * When events come in while the container is loading, these will be ignored until the container finished loading.
   * These events are instead pushed to a queue that is processed after the loading is over. 
   * Once 
   */
  protected updateQueue = () => {
    if(!this.eventQueue.length) return;
    this.disposer   = null;             // Reset disposed so a new one can be registered if needed
    const queue     = this.eventQueue;
    this.eventQueue = [];               // Clear the queue since new events could come in while this is processed

    queue.forEach(e => {
      this.update(e.data, e.method);
    });
  }

  /**
   * Page navigation functions
   */
  goToPage = (page: number) => {
    this.page = page;
    this.load();
  }

  nextPage = () => {
    this.page++;
    this.load();
  }

  prevPage = () => {
    this.page--;
    this.load();
  }
}
